import { Button } from "@hex-insights/core";
import { AddSubFormButtonRenderProps, Form, SubFormField, SubFormRenderProps, TextField } from "@hex-insights/forms";
import {
	HomeRoomSectionFormValues,
	StudentHomeRoomSectionEnrollmentFormValues,
	TeacherHomeRoomSectionEnrollmentFormValues,
} from "../../FormUtilities";
import { AcademicTermSelectField } from "../AcademicTerm";
import { HomeRoomSelectField } from "../HomeRoom";
import { RoomSelectField } from "../Room";
import { BaseFieldProps } from "../shared";
import { StudentHomeRoomSectionEnrollmentForm } from "../StudentHomeRoomSectionEnrollment";
import { TeacherHomeRoomSectionEnrollmentForm } from "../TeacherHomeRoomSectionEnrollment";

/**
 * Generic props for fields of the HomeRoomSection model.
 */
type FieldProps<K extends keyof HomeRoomSectionFormValues.Base = keyof HomeRoomSectionFormValues.Base> = BaseFieldProps<
	Pick<HomeRoomSectionFormValues.Base, K>
>;

/**
 * Generic props for fields of the HomeRoomSection model that only appear in the create form.
 */
type CreateFieldProps<K extends keyof HomeRoomSectionFormValues.Create = keyof HomeRoomSectionFormValues.Create> =
	BaseFieldProps<Pick<HomeRoomSectionFormValues.Create, K>>;

/**
 * Field component for the `namePrefix` field of the HomeRoomSection model.
 */
export function NamePrefix({ formState, ...props }: FieldProps<"namePrefix">) {
	return <TextField formState={formState} name="namePrefix" optional allowTrailingWhitespace {...props} />;
}

/**
 * Field component for the `nameSuffix` field of the HomeRoomSection model.
 */
export function NameSuffix({ formState, ...props }: FieldProps<"nameSuffix">) {
	return <TextField formState={formState} name="nameSuffix" optional allowLeadingWhitespace {...props} />;
}

export namespace AcademicTerm {
	export type SelectProps = FieldProps<"academicTermID"> &
		Omit<AcademicTermSelectField.SelectProps<HomeRoomSectionFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `academicTerm` edge of the HomeRoomSection model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <AcademicTermSelectField.Select formState={formState} name="academicTermID" {...props} />;
	}
}

export namespace HomeRoom {
	export type SelectProps = FieldProps<"homeRoomID"> &
		Omit<HomeRoomSelectField.SelectProps<HomeRoomSectionFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `homeRoom` edge of the HomeRoomSection model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <HomeRoomSelectField.Select formState={formState} name="homeRoomID" {...props} />;
	}
}

export namespace Room {
	export type SelectProps = FieldProps<"roomID"> &
		Omit<RoomSelectField.SelectProps<HomeRoomSectionFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `room` edge of the HomeRoomSection model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <RoomSelectField.Select formState={formState} name="roomID" optional {...props} />;
	}
}

export namespace StudentHomeRoomSectionEnrollments {
	/**
	 * SubForm field component for the `studentHomeRoomSectionEnrollments` edge of the HomeRoomSection model.
	 */
	export function SubForm({ formState, ...props }: CreateFieldProps<"studentHomeRoomSectionEnrollments">) {
		return (
			<SubFormField
				formState={formState}
				name="studentHomeRoomSectionEnrollments"
				blankItem={StudentHomeRoomSectionEnrollmentFormValues.blankItemInHomeRoomSection}
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</SubFormField>
		);
	}

	SubForm.SubForm = function ({
		formState,
		onRemoveClick,
	}: SubFormRenderProps<StudentHomeRoomSectionEnrollmentFormValues.CreateInHomeRoomSection>) {
		return (
			<Form.Column>
				<Button variant="tertiary" size="small" onClick={onRemoveClick} disabled={formState.disabled}>
					Remove Student Home Room Section Enrollment
				</Button>
				<StudentHomeRoomSectionEnrollmentForm.CreateFieldsInHomeRoomSection formState={formState} />
			</Form.Column>
		);
	};

	SubForm.AddButton = function ({
		onClick,
		disabled,
	}: AddSubFormButtonRenderProps<
		Pick<HomeRoomSectionFormValues.Create, "studentHomeRoomSectionEnrollments">,
		StudentHomeRoomSectionEnrollmentFormValues.CreateInHomeRoomSection
	>) {
		return (
			<Button variant="tertiary" size="small" onClick={onClick} disabled={disabled}>
				Add Student Home Room Section Enrollment
			</Button>
		);
	};
}

export namespace TeacherHomeRoomSectionEnrollments {
	/**
	 * SubForm field component for the `teacherHomeRoomSectionEnrollments` edge of the HomeRoomSection model.
	 */
	export function SubForm({ formState, ...props }: CreateFieldProps<"teacherHomeRoomSectionEnrollments">) {
		return (
			<SubFormField
				formState={formState}
				name="teacherHomeRoomSectionEnrollments"
				blankItem={TeacherHomeRoomSectionEnrollmentFormValues.blankItemInHomeRoomSection}
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</SubFormField>
		);
	}

	SubForm.SubForm = function ({
		formState,
		onRemoveClick,
	}: SubFormRenderProps<TeacherHomeRoomSectionEnrollmentFormValues.CreateInHomeRoomSection>) {
		return (
			<Form.Column>
				<Button variant="tertiary" size="small" onClick={onRemoveClick} disabled={formState.disabled}>
					Remove Teacher Home Room Section Enrollment
				</Button>
				<TeacherHomeRoomSectionEnrollmentForm.CreateFieldsInHomeRoomSection formState={formState} />
			</Form.Column>
		);
	};

	SubForm.AddButton = function ({
		onClick,
		disabled,
	}: AddSubFormButtonRenderProps<
		Pick<HomeRoomSectionFormValues.Create, "teacherHomeRoomSectionEnrollments">,
		TeacherHomeRoomSectionEnrollmentFormValues.CreateInHomeRoomSection
	>) {
		return (
			<Button variant="tertiary" size="small" onClick={onClick} disabled={disabled}>
				Add Teacher Home Room Section Enrollment
			</Button>
		);
	};
}
